<template>
  <a
    ref="anchor"
    :href="
      !disabled
        ? `${props.link.url}?culture=${app.culture}${consentQuery}`
        : undefined
    "
    @click.prevent="clickEventThenRedirect"
  >
    <Row
      items="start"
      gap="md"
      :class="[
        `group py-4 transition-colors duration-300 sm:py-8`,
        { 'hover:bg-gray-50': !disabled },
        { 'opacity-60 grayscale': disabled },
      ]"
    >
      <div class="hidden sm:block">
        <ActivityCardPortrait :link />
      </div>
      <Col gap="sm" stretch>
        <Row justify="between" items="start" gap="xs">
          <Row gap="xs" wrap>
            <Badge v-if="dateRange" variant="transparent" :icon="ClockIcon">
              {{ dateRange }}
            </Badge>
            <Badge v-if="locations" variant="transparent" :icon="MapPinIcon">
              {{ locations }}
            </Badge>
          </Row>
          <Row wrap gap="xs" justify="end" class="shrink-0">
            <Badge v-if="available" variant="tenant">
              {{ available }}
            </Badge>
            <Badge v-if="link.isOnline" variant="tenant" :icon="WifiIcon">
              {{ app.texts.activity.online }}
            </Badge>
            <Badge v-if="showType" class="hidden sm:flex">{{
              link.type
            }}</Badge>
          </Row>
        </Row>
        <Row items="center" gap="sm">
          <div class="block sm:hidden">
            <ActivityCardPortrait :link :disabled />
          </div>
          <div class="text-xl font-normal sm:text-xl">
            {{ link.name }}
          </div>
        </Row>
        <span
          v-if="link.description"
          class="description prose prose-sm prose-gray prose-a:text-tenant"
          v-html="link.description"
        ></span>
        <a v-if="!disabled"
          ><Link>{{ `${app.texts.activity.registrationLink} →` }}</Link>
        </a>
      </Col>
    </Row>
  </a>
</template>

<script setup lang="ts">
import {
  IPublicActivityLocationDTO,
  IPublicActivityRegistrationLinkDTO,
} from "lib/eduConfigurationServiceClient";
import ActivityCardPortrait from "./ActivityCardPortrait.vue";
import { ClockIcon, MapPinIcon, WifiIcon } from "@heroicons/vue/24/outline";

import { DateTime, Interval } from "luxon";
import { computed, onMounted, ref } from "vue";
import {
  getEncodedConsent,
  getLastConsent,
  pushEventToDatalayer,
} from "@/views/components/consent-banner/ConsentBanner.utils";
import { app } from "@/app.context";

const props = defineProps<{
  link: IPublicActivityRegistrationLinkDTO;
  showType: boolean;
  activityLocations?: IPublicActivityLocationDTO[];
}>();

const disabled = computed(() => props.link.remainingCapacity === 0);

const locations = computed<string | undefined>(() => {
  const uniqueNames = new Set<string>();
  const uniqueCities = new Set<string>();

  props.link.activityLocationIds.forEach((locationId) => {
    const location = props.activityLocations?.find(
      (location) => location.id === locationId,
    );

    if (location?.name) {
      uniqueNames.add(location.name);
    }

    if (location?.city) {
      uniqueCities.add(location.city);
    }
  });

  if (uniqueNames.size === 0 || uniqueCities.size === 0) {
    return undefined;
  }

  if (uniqueNames.size === 1) {
    return uniqueNames.values().next().value;
  }

  return uniqueCities.size > 0
    ? Array.from(uniqueCities).join(", ")
    : undefined;
});

const dateRange = computed<string>(() =>
  Interval.fromDateTimes(
    props.link.startDateTime,
    props.link.endDateTime,
  ).toLocaleString(DateTime.DATETIME_MED),
);

const available = computed<string | undefined>(() =>
  props.link.remainingCapacity !== undefined
    ? `${props.link.remainingCapacity} ${app.texts.activity.available}`
    : undefined,
);

const anchor = ref<HTMLAnchorElement | null>(null);

const consent = getLastConsent(window.dataLayer);
const consentQuery = consent ? `&consent=${getEncodedConsent(consent)}` : "";

function clickEventThenRedirect() {
  if (!anchor.value) throw new Error("Anchor ref is not set");

  if (consent) {
    pushEventToDatalayer("click", {
      eventCategory: "click-registration-link",
      eventAction: "click",
      eventLabel: props.link.id,
      registrationLinkId: props.link.id,
      registrationLinkName: props.link.name,
      activityId: props.link.activityId,
      activityName: props.link.activityName,
    });
  }

  window.open(anchor.value.href, "_self");
}

onMounted(() => {
  if (!anchor.value) throw new Error("Anchor ref is not set");

  const linksInDescription = anchor.value.querySelector(".description a");

  // Don't bubble click events for links in description to the parent element
  if (linksInDescription) {
    linksInDescription.addEventListener("click", (event) => {
      event.stopPropagation();
    });
  }
});
</script>
